export interface IGameService {
  getGameProviders: () => Promise<[] | void>
  getFreeSpinGames: () => Promise<[] | void>
}

export type GameSearchParam = {
  page: number
  pageSize: number
  category?: string
  provider?: string
  search?: string
  gameIds?: string[]
  isLive?: boolean
  isGameShow?: boolean
  providerIds?: string[]
  includeDisabled?: boolean
}

export interface GameSectionsInterface {
  slots: GameSectionsResponseInterface
  live: GameSectionsResponseInterface
  new: GameSectionsResponseInterface
  shows: GameSectionsResponseInterface
  recent: GameSectionsResponseInterface
}

export interface GameSectionsResponseInterface {
  games: GameInterface[]
  page: number
  total_pages: number
  total: number
}

export interface GameInterface {
  id?: number
  name?: string
  category: string
  description: string
  /** Per-locale SEO; the shape a category uses. */
  seo?: CategorySeo
  producer: "NUX" | "DARK" | "ORIGINALS"
  provider: string
  gameProvider: ProviderInterface
  title: string
  identifier: string
  identifier2: string
  image: string
  image_vertical: string
  image_override: string | null
  image_vertical_override: string | null
  image_provider: string
  category_icon: string
  has_freespins: boolean
  bonus_buy: true
  hd: boolean
  payout: number
  theme: string
  feature_group: string
  is_favourite: boolean
  demo: boolean

  // new added
  created_at: string
  priority: number
  device: "All device" | "Desktop" | "Mobile"
  is_game_show: boolean
  is_live: boolean
  enabled: boolean
  type: string
  color: string

  // availableCurrencies: true,
  blockedCountries: string[]
  restrictedCountries: string[]

  additionalGames: GameInterface[]
}

export interface ProviderInterface {
  title: string
  identifier: string
  restrictions: string | null
  logo: string
  priority: number
  enabled: true
  image: string
  color: string
}

export type UpdateStatus = {
  lastManualRunAt: string | null
  lastManualTriggeredByUserId?: string | null
  lastCronRunAt: string | null
  nextAllowedAt: string | null
  startedAt?: string | null
  inProgress: boolean
  allowed: boolean
}

export interface SectionOrderGame {
  identifier: string
  title: string
  image: string | null
  image_vertical: string | null
  image_override: string | null
  image_vertical_override: string | null
  enabled: boolean
  provider: string
  gameProvider: {
    identifier: string
    title: string
  } | null
}

export interface SectionOrderResponse {
  sectionKey: string
  geo: string
  isFork: boolean
  gameIds: string[]
  page: number
  pageSize: number
  total: number
  total_pages: number
  pageGames: SectionOrderGame[]
}

export interface HomepageCategoryItem {
  id: string
  key: string
  title: Record<string, string>
  icon_key: string | null
  source_type: "BUILTIN" | "MANUAL" | "RULE"
  is_orderable: boolean
  category_enabled: boolean
  position: number
  enabled: boolean
}

export interface HomepageOrderResponse {
  items: HomepageCategoryItem[]
  geo: string
  isFork: boolean
}

// Languages supported by the player FE — keep in sync with frontend2/messages/*.json.
// Mirrors the player frontend's SUPPORTTED_LOCALES (src/config/locales.ts).
// A category page is an indexed landing page per locale, so a missing title
// here means an English <h1> on a non-English URL.
export const PLAYER_LOCALES = ["en", "es", "fr", "hr", "pt", "de", "pl", "ru", "se", "dk", "ar", "sw"] as const
export type PlayerLocale = (typeof PLAYER_LOCALES)[number]

/** Per-locale SEO overrides. Blank fields fall back to generated copy on the FE. */
export interface CategorySeoFooter {
  html: string
  collapsed: boolean
}

export interface CategorySeoEntry {
  title?: string
  description?: string
  h1?: string
  /** SEO copy below the category page, collapsible by the visitor. */
  footer?: CategorySeoFooter
}

export interface CategorySeo extends Partial<Record<PlayerLocale, CategorySeoEntry>> {
  noindex?: boolean
}

export interface CategoryRecord {
  id: string
  key: string
  /** Public URL segment: /casino/<slug> on the player site. */
  slug: string
  seo: CategorySeo | null
  title: Partial<Record<PlayerLocale, string>> | null
  icon_key: string | null
  source_type: "BUILTIN" | "MANUAL" | "RULE"
  source_payload: unknown
  enabled: boolean
  is_orderable: boolean
  member_count: number
  builtin_qualifying_count: number | null
  /** Whether a carousel is placed on the lobby — not the same as being routable. */
  on_homepage: boolean
}

export interface CategoryCreateInput {
  key: string
  /** Omit to let the backend derive it from title.en. */
  slug?: string
  title: Partial<Record<PlayerLocale, string>>
  icon_key: string | null
  seo?: CategorySeo
  enabled?: boolean
}

export interface CategoryUpdateInput {
  slug?: string
  title?: Partial<Record<PlayerLocale, string>>
  icon_key?: string | null
  seo?: CategorySeo
  enabled?: boolean
}
